<template>
  <div
    class="flex items-start gap-2"
    :class="isUser ? 'justify-end' : 'justify-start'"
  >
    <!-- User bubble -->
    <div
      v-if="isUser"
      class="user-bubble whitespace-pre-wrap text-sm"
    >
      <div
        v-if="message.attachmentName"
        class="user-bubble-attachment"
      >
        📎 {{ message.attachmentName }}
        <!-- A reopened saved chat keeps only the file's name, not its text. -->
        <div
          v-if="message.reopened"
          class="reopened-attachment-note"
        >
          This file was attached earlier. Attach it again to ask about it.
        </div>
      </div>
      {{ message.content }}
    </div>

    <!-- Assistant: avatar + bubble with markdown / chart-pill segments -->
    <template v-else>
      <span class="cap-avatar mt-1">
        <img
          src="@/assets/capacio/login/logo-cropped.svg"
          alt=""
          class="cap-bean"
        >
      </span>
      <div class="assistant-bubble">
        <template v-for="(segment, i) in segments">
          <div
            v-if="segment.type === 'md'"
            :key="i"
            class="markdown"
            v-html="segment.html"
          />
          <button
            v-else
            :key="i"
            class="chart-pill"
            @click="$emit('focus-sources')"
          >
            📊 {{ segment.player }} profile <span class="pill-arrow">→</span>
          </button>
        </template>

        <!-- Thumbs up/down: only on answers that were saved (they have an id) -->
        <div
          v-if="feedbackEnabled && message.id"
          class="cai-feedback"
        >
          <button
            class="cai-feedback__btn"
            :class="{ 'cai-feedback__btn--on': message.rating === 1 }"
            title="Good answer"
            aria-label="Good answer"
            @click="rate(1)"
          >
            👍
          </button>
          <button
            class="cai-feedback__btn"
            :class="{ 'cai-feedback__btn--on': message.rating === -1 }"
            title="Bad answer"
            aria-label="Bad answer"
            @click="rate(-1)"
          >
            👎
          </button>
          <span
            v-if="thanks"
            class="cai-feedback__thanks"
          >Thanks for the feedback</span>
        </div>
        <div
          v-if="commentOpen"
          class="cai-feedback__comment"
        >
          <textarea
            v-model="comment"
            maxlength="2000"
            rows="2"
            placeholder="What was wrong? (optional)"
          />
          <div class="cai-feedback__comment-buttons">
            <button
              class="cai-feedback__send"
              aria-label="Send feedback"
              @click="sendComment"
            >
              Send
            </button>
            <button
              class="cai-feedback__skip"
              @click="commentOpen = false"
            >
              Skip
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { marked } from 'marked'
import DOMPurify from 'dompurify'

marked.setOptions({ headerIds: false, mangle: false })

export default {
  props: {
    message: {
      type: Object,
      required: true
    },
    feedbackEnabled: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      commentOpen: false,
      comment: '',
      thanks: false
    }
  },

  computed: {
    isUser () {
      return this.message.role === 'user'
    },

    // Split the reply on inline [[CHART:Player N]] tokens: markdown runs become
    // sanitized HTML, each token becomes a clickable chart pill.
    segments () {
      const content = this.message.content || ''
      const re = /\[\[CHART:([^\]]+)\]\]/g
      const out = []
      let cursor = 0
      let m
      while ((m = re.exec(content)) !== null) {
        if (m.index > cursor) {
          out.push({ type: 'md', html: this.renderMarkdown(content.slice(cursor, m.index)) })
        }
        out.push({ type: 'chart', player: m[1].trim() })
        cursor = m.index + m[0].length
      }
      if (cursor < content.length) {
        out.push({ type: 'md', html: this.renderMarkdown(content.slice(cursor)) })
      }
      return out
    }
  },

  methods: {
    // Saves at once; a thumbs down also offers an optional comment.
    rate (rating) {
      this.$emit('feedback', { message: this.message, rating, comment: null })
      this.commentOpen = rating === -1
      this.comment = ''
      this.thanks = rating === 1
    },

    sendComment () {
      const comment = this.comment.trim()
      if (comment) this.$emit('feedback', { message: this.message, rating: -1, comment })
      this.commentOpen = false
      this.thanks = true
    },

    renderMarkdown (text) {
      return DOMPurify.sanitize(marked.parse(text))
    }
  }
}
</script>
